'use client';

import { Elements } from '@/components/elements';
import clsx from 'clsx';
import Image, { type StaticImageData } from 'next/image';
import { useEffect, useId, useMemo, useState } from 'react';
import QuoteIcon from '@icons/quote.svg';

export type WhatBrokersAreSayingSlide = {
    quote: string;
    avatarSrc: string | StaticImageData;
    avatarAlt?: string;
    roleTitle: string;
    companyLine: string;
};

export type WhatBrokersAreSayingProps = {
    slides: WhatBrokersAreSayingSlide[];
    tagLabel?: string;
    title?: string;
    className?: string;
    autoPlay?: boolean;
    /** Duration (ms) for one full loop of all slides. Omit for a default based on slide count. */
    autoplayIntervalMs?: number;
};

const CARD_PX = 550;
const GAP_PX = 24;
const VIEWPORT_W = CARD_PX * 3 + GAP_PX * 2;
const DEFAULT_LOOP_MS_PER_CARD = 8000;

function setWidthPx(n: number): number {
    if (n <= 0) return 0;
    return n * CARD_PX + (n - 1) * GAP_PX;
}

function TestimonialCarouselCard({ slide }: { slide: WhatBrokersAreSayingSlide }) {
    return (
        <article
            className="flex h-full min-h-[280px] flex-col rounded-2xl bg-brand-50 p-5 sm:p-8"
        >
            <div className="flex min-h-0 flex-1 flex-col items-center gap-6">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-100">
                    <Image src={QuoteIcon} alt="" width={24} height={24} />
                </div>
                <blockquote className="w-full text-center text-sm italic leading-relaxed text-gray-700 sm:text-base md:text-lg">
                    {slide.quote}
                </blockquote>
                <div className="mt-auto flex w-full shrink-0 justify-center pt-4">
                    <div className="flex items-center gap-4">
                        <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-gray-100 ring-2 ring-gray-100">
                            <Image
                                src={slide.avatarSrc}
                                alt={slide.avatarAlt ?? ''}
                                fill
                                className="object-cover"
                                sizes="40px"
                            />
                        </div>
                        <div className="min-w-0 text-left">
                            <p className="text-base font-semibold text-gray-900">{slide.roleTitle}</p>
                            <p className="text-sm text-gray-600">{slide.companyLine}</p>
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
}

export function WhatBrokersAreSaying({
    slides,
    tagLabel = 'TESTIMONIALS',
    title = 'What Brokers Are Saying',
    className,
    autoPlay = true,
    autoplayIntervalMs,
}: WhatBrokersAreSayingProps) {
    const reactId = useId().replace(/:/g, '');
    const [isPointerOver, setIsPointerOver] = useState(false);
    const [reduceMotion, setReduceMotion] = useState(false);

    const n = slides.length;
    const setW = setWidthPx(n);

    const loopDurationMs =
        autoplayIntervalMs != null && autoplayIntervalMs > 0
            ? autoplayIntervalMs
            : Math.max(12000, n * DEFAULT_LOOP_MS_PER_CARD);

    const keyframesCss = useMemo(() => {
        const name = `wbMarquee_${reactId}`;
        return {
            name,
            css: `@keyframes ${name}{from{transform:translate3d(0,0,0)}to{transform:translate3d(-${setW}px,0,0)}}`,
        };
    }, [reactId, setW]);

    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = () => setReduceMotion(mq.matches);
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    if (slides.length === 0) return null;

    const runMarquee = autoPlay && n > 1 && !reduceMotion && setW > 0;
    const trackItems = runMarquee ? [...slides, ...slides] : slides;

    return (
        <section className={clsx('overflow-x-hidden py-16 md:py-20', className)} aria-label={title}>
            {runMarquee ? <style dangerouslySetInnerHTML={{ __html: keyframesCss.css }} /> : null}

            <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-10 px-5 md:px-10 lg:px-[79px]">
                <div className="flex flex-col items-center gap-4 text-center">
                    <span className="inline-flex rounded-lg bg-brand-100 px-2 py-1 text-base font-medium tracking-[0.12em] text-brand-700 uppercase">
                        {tagLabel}
                    </span>
                    <Elements.Title
                        title={title}
                        type="h2"
                        className="max-w-[920px] text-3xl font-semibold md:text-4xl"
                    />
                </div>
            </div>

            {n > 1 ? (
                <div
                    className="flex w-full justify-center py-4 md:py-6"
                    onMouseEnter={() => setIsPointerOver(true)}
                    onMouseLeave={() => setIsPointerOver(false)}
                    role="group"
                    aria-label="Testimonials, continuous carousel. Hover to pause."
                >
                    <div
                        className="max-w-full overflow-hidden"
                        style={{ width: `min(100%, ${VIEWPORT_W}px)` }}
                    >
                        <div
                            className="flex w-max will-change-transform"
                            style={{
                                gap: GAP_PX,
                                animation: runMarquee
                                    ? `${keyframesCss.name} ${loopDurationMs}ms linear infinite`
                                    : undefined,
                                animationPlayState:
                                    runMarquee && !isPointerOver ? 'running' : runMarquee ? 'paused' : undefined,
                            }}
                        >
                            {trackItems.map((slide, i) => (
                                <div
                                    key={i}
                                    className="shrink-0"
                                    style={{ width: CARD_PX, minWidth: CARD_PX }}
                                >
                                    <TestimonialCarouselCard slide={slide} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="mx-auto flex w-full justify-center px-5 pt-4 md:px-10 lg:px-[79px]">
                    <div style={{ width: CARD_PX, minWidth: CARD_PX }}>
                        <TestimonialCarouselCard slide={slides[0]} />
                    </div>
                </div>
            )}
        </section>
    );
}
